import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";

const STEPS = ["Details", "Upload files", "Thresholds", "Frequency", "Confirm"];
const iCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

// CPI/SPI stored as percentage strings ("94.5" = 94.5%), Staffing as integer %,
// CEI as decimal strings ("0.90"). Converted to decimals in handleCreate.
const DEFAULT_THRESHOLDS = {
  cv_threshold:         "500000",
  variance_pct:         "5",
  fte_hours_per_week:   "30",
  fte_hours_per_month:  "160",
  cpi_green_min:        "94.5",
  cpi_green_max:        "104.5",
  cpi_yellow_min:       "84.5",
  spi_green_min:        "94.5",
  spi_green_max:        "104.5",
  spi_yellow_min:       "84.5",
  cei_yellow_min:       "0.90",
  cei_green_min:        "1.00",
  staffing_risk_min:    "51",
  staffing_healthy_min: "76",
} as const;

type TKey = keyof typeof DEFAULT_THRESHOLDS;
type Thresholds = Record<TKey, string>;

// Inline transparent input — looks like plain colored text, no box
function InlineInput({ value, onChange, color, minWidth = "2.5ch" }: {
  value: string; onChange: (v: string) => void;
  color: "blue" | "green" | "amber" | "red"; minWidth?: string;
}) {
  const textColor = {
    blue: "text-blue-600", green: "text-green-600",
    amber: "text-amber-700", red: "text-red-600",
  }[color];
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`border-none bg-transparent p-0 m-0 focus:outline-none ${textColor} font-bold text-sm leading-none`}
      style={{ width: `${Math.max(parseFloat(minWidth), value.length + 0.5)}ch` }}
    />
  );
}

export default function CreateProject() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [details, setDetails] = useState({ name: "", aircraft_platform: "", bac: "", start_date: "", description: "" });
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [thresholds, setThresholds] = useState<Thresholds>({ ...DEFAULT_THRESHOLDS });
  const set = (k: TKey, v: string) => setThresholds(t => ({ ...t, [k]: v }));
  const [frequency, setFrequency] = useState<"monthly" | "quarterly">("monthly");

  const canContinue = () => {
    if (step === 1) return details.name.trim() !== "" && details.description.trim() !== "";
    return true;
  };

  const handleCreate = async () => {
    setLoading(true); setError("");
    try {
      const nameParts = details.name.split(" ");
      const res = await api.post("/api/projects", {
        name: details.name, code: nameParts[0] || "M000",
        bp_code: nameParts[1] || "BP000", status: "active",
      });
      const projectId = res.data.id;
      // CPI/SPI stored as % → divide by 100; CEI stored as decimal; Staffing % → divide by 100
      await api.put(`/api/projects/${projectId}/config/thresholds`, {
        cv_threshold:         parseFloat(thresholds.cv_threshold),
        variance_pct:         parseFloat(thresholds.variance_pct),
        fte_hours_per_week:   parseFloat(thresholds.fte_hours_per_week),
        fte_hours_per_month:  parseFloat(thresholds.fte_hours_per_month),
        cpi_green_min:        parseFloat(thresholds.cpi_green_min) / 100,
        cpi_green_max:        parseFloat(thresholds.cpi_green_max) / 100,
        cpi_yellow_min:       parseFloat(thresholds.cpi_yellow_min) / 100,
        spi_green_min:        parseFloat(thresholds.spi_green_min) / 100,
        spi_green_max:        parseFloat(thresholds.spi_green_max) / 100,
        spi_yellow_min:       parseFloat(thresholds.spi_yellow_min) / 100,
        cei_yellow_min:       parseFloat(thresholds.cei_yellow_min),
        cei_green_min:        parseFloat(thresholds.cei_green_min),
        staffing_risk_min:    parseFloat(thresholds.staffing_risk_min) / 100,
        staffing_healthy_min: parseFloat(thresholds.staffing_healthy_min) / 100,
      });
      localStorage.setItem("active_project_id", projectId);
      navigate("/select-project");
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to create project");
    } finally { setLoading(false); }
  };

  // Card base styles per color
  const card = (c: "blue" | "green" | "amber" | "red") => {
    const s: Record<string, string> = {
      blue:  "border-blue-200  bg-blue-50",
      green: "border-green-200 bg-green-50",
      amber: "border-amber-200 bg-amber-50",
      red:   "border-red-200   bg-red-50",
    };
    return `rounded-2xl border-2 ${s[c]} p-4 flex flex-col gap-2`;
  };
  const dot = (c: "blue" | "green" | "amber" | "red") => {
    const s: Record<string, string> = {
      blue: "bg-blue-500", green: "bg-green-500", amber: "bg-amber-400", red: "bg-red-500",
    };
    return `w-2.5 h-2.5 rounded-full flex-shrink-0 ${s[c]}`;
  };
  const label = (c: "blue" | "green" | "amber" | "red") => {
    const s: Record<string, string> = {
      blue: "text-blue-700", green: "text-green-700", amber: "text-amber-700", red: "text-red-700",
    };
    return `text-sm font-bold ${s[c]}`;
  };
  const rangeText = (c: "blue" | "green" | "amber" | "red") => {
    const s: Record<string, string> = {
      blue: "text-blue-600", green: "text-green-600", amber: "text-amber-700", red: "text-red-600",
    };
    return `text-sm font-bold ${s[c]} flex items-baseline gap-0 flex-wrap`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[900px]">

        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {["Project details", "Upload project files", "Thresholds & filters", "Report frequency", "Review & create"][step - 1]}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {step === 1 && "Basic information about this program — used to label all generated reports."}
                {step === 2 && "Provide supporting documents so the AI has baseline context for this program."}
                {step === 3 && "Default values for this project — can be overridden per report."}
                {step === 4 && "How often do you plan to generate EAC reports for this project?"}
                {step === 5 && "Confirm your project setup before creating."}
              </p>
            </div>
            <button onClick={() => navigate("/select-project")} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center flex-wrap gap-y-1">
            {STEPS.map((s, i) => {
              const n = i + 1; const done = n < step; const active = n === step;
              return (
                <div key={s} className="flex items-center">
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
                    ${done ? "text-green-600" : active ? "text-blue-600 bg-blue-50" : "text-gray-400"}`}>
                    {done
                      ? <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      : <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"}`}>{n}</div>
                    }
                    <span>{s}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`w-5 h-px mx-1 ${done ? "bg-green-400" : "bg-gray-200"}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-6">

          {/* Step 1 — Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Project name *</label>
                  <input value={details.name} onChange={e => setDetails(d => ({ ...d, name: e.target.value }))}
                    placeholder="e.g. M170 BP884 — Business & Regional Avionics" className={iCls} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Aircraft platform</label>
                  <input value={details.aircraft_platform} onChange={e => setDetails(d => ({ ...d, aircraft_platform: e.target.value }))}
                    placeholder="e.g. Bombardier Global 7500" className={iCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Contract value (BAC)</label>
                  <input value={details.bac} onChange={e => setDetails(d => ({ ...d, bac: e.target.value }))}
                    placeholder="e.g. $50,000,000" className={iCls} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Program start date</label>
                  <input type="date" value={details.start_date} onChange={e => setDetails(d => ({ ...d, start_date: e.target.value }))} className={iCls} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Description *</label>
                <textarea value={details.description} onChange={e => setDetails(d => ({ ...d, description: e.target.value }))}
                  placeholder="Brief description of the program scope, deliverables, and key milestones..."
                  rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
            </div>
          )}

          {/* Step 2 — Upload */}
          {step === 2 && (
            <div>
              <div onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]); }}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                  ${dragging ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}>
                <span className="absolute top-3 right-3 text-xs font-medium text-gray-500 border border-gray-300 rounded-full px-2 py-0.5">Optional</span>
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">Upload</p>
                <p className="text-xs text-gray-400 mb-4">All supporting documents (PPTs, PDFs, requirement docs, planning files, etc.)</p>
                <div className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Click to browse or drop file
                </div>
                <input ref={fileInputRef} type="file" multiple className="hidden"
                  onChange={e => { if (e.target.files) setFiles(prev => [...prev, ...Array.from(e.target.files!)]); }} />
              </div>
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-700">{f.name} <span className="text-xs text-gray-400">({(f.size / 1024).toFixed(1)} KB)</span></span>
                      <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 flex items-start gap-2 text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                These files give the AI baseline context for variance explanations. You can add more files from Project Settings at any time.
              </div>
            </div>
          )}

          {/* Step 3 — Thresholds */}
          {step === 3 && (
            <div className="space-y-4 max-h-[58vh] overflow-y-auto pr-1">

              {/* Top row: Flagging + FTE */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-100 border border-gray-200 rounded-xl p-4 flex flex-col justify-between">
                  <p className="text-sm font-semibold text-gray-800 mb-3">Threshold for flagging</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Dollar amount</label>
                      <input value={thresholds.cv_threshold} onChange={e => set("cv_threshold", e.target.value)}
                        placeholder="500000" className={iCls} />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Or percentage</label>
                      <div className="relative">
                        <input value={thresholds.variance_pct} onChange={e => set("variance_pct", e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-100 border border-gray-200 rounded-xl p-4 flex flex-col justify-between">
                  <p className="text-sm font-semibold text-gray-800 mb-0.5">Full-Time Equivalent (FTE)</p>
                  <p className="text-[10px] text-gray-400 mb-3">1 FTE — set hours that define 1 FTE</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">hrs/week</label>
                      <input value={thresholds.fte_hours_per_week} onChange={e => set("fte_hours_per_week", e.target.value)} className={iCls} />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">hrs/month</label>
                      <input value={thresholds.fte_hours_per_month} onChange={e => set("fte_hours_per_month", e.target.value)} className={iCls} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Threshold for flagging — metric groups */}
              <div className="bg-gray-100 border border-gray-200 rounded-xl p-5 space-y-5">
                <p className="text-sm font-semibold text-gray-800">Threshold for flagging</p>

                {/* CPI + SPI */}
                <div className="grid grid-cols-2 gap-5">

                  {/* CPI */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-xs font-medium text-gray-500 mb-3">CPI thresholds</p>
                    <div className="grid grid-cols-2 gap-2">

                      {/* Blue — editable: green_max */}
                      <div className={card("blue")}>
                        <div className="flex items-center gap-2"><span className={dot("blue")} /><span className={label("blue")}>Blue</span></div>
                        <div className={rangeText("blue")}>
                          <span>&gt;&nbsp;</span>
                          <InlineInput value={thresholds.cpi_green_max} onChange={v => set("cpi_green_max", v)} color="blue" />
                          <span>%</span>
                        </div>
                      </div>

                      {/* Green — editable: green_min; derived upper = green_max */}
                      <div className={card("green")}>
                        <div className="flex items-center gap-2"><span className={dot("green")} /><span className={label("green")}>Green</span></div>
                        <div className={rangeText("green")}>
                          <InlineInput value={thresholds.cpi_green_min} onChange={v => set("cpi_green_min", v)} color="green" />
                          <span>%&nbsp;–&nbsp;{thresholds.cpi_green_max}%</span>
                        </div>
                      </div>

                      {/* Yellow — editable: yellow_min; derived upper = green_min */}
                      <div className={card("amber")}>
                        <div className="flex items-center gap-2"><span className={dot("amber")} /><span className={label("amber")}>Yellow</span></div>
                        <div className={rangeText("amber")}>
                          <InlineInput value={thresholds.cpi_yellow_min} onChange={v => set("cpi_yellow_min", v)} color="amber" />
                          <span>%&nbsp;–&nbsp;{thresholds.cpi_green_min}%</span>
                        </div>
                      </div>

                      {/* Red — derived */}
                      <div className={card("red")}>
                        <div className="flex items-center gap-2"><span className={dot("red")} /><span className={label("red")}>Red</span></div>
                        <p className="text-sm font-bold text-red-600">&lt;&nbsp;{thresholds.cpi_yellow_min}%</p>
                      </div>
                    </div>
                  </div>

                  {/* SPI */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-xs font-medium text-gray-500 mb-3">SPI thresholds</p>
                    <div className="grid grid-cols-2 gap-2">

                      <div className={card("blue")}>
                        <div className="flex items-center gap-2"><span className={dot("blue")} /><span className={label("blue")}>Blue</span></div>
                        <div className={rangeText("blue")}>
                          <span>&gt;&nbsp;</span>
                          <InlineInput value={thresholds.spi_green_max} onChange={v => set("spi_green_max", v)} color="blue" />
                          <span>%</span>
                        </div>
                      </div>

                      <div className={card("green")}>
                        <div className="flex items-center gap-2"><span className={dot("green")} /><span className={label("green")}>Green</span></div>
                        <div className={rangeText("green")}>
                          <InlineInput value={thresholds.spi_green_min} onChange={v => set("spi_green_min", v)} color="green" />
                          <span>%&nbsp;–&nbsp;{thresholds.spi_green_max}%</span>
                        </div>
                      </div>

                      <div className={card("amber")}>
                        <div className="flex items-center gap-2"><span className={dot("amber")} /><span className={label("amber")}>Yellow</span></div>
                        <div className={rangeText("amber")}>
                          <InlineInput value={thresholds.spi_yellow_min} onChange={v => set("spi_yellow_min", v)} color="amber" />
                          <span>%&nbsp;–&nbsp;{thresholds.spi_green_min}%</span>
                        </div>
                      </div>

                      <div className={card("red")}>
                        <div className="flex items-center gap-2"><span className={dot("red")} /><span className={label("red")}>Red</span></div>
                        <p className="text-sm font-bold text-red-600">&lt;&nbsp;{thresholds.spi_yellow_min}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CEI + Staffing */}
                <div className="grid grid-cols-2 gap-5">

                  {/* CEI */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-xs font-medium text-gray-500 mb-3">CEI thresholds</p>
                    <div className="grid grid-cols-3 gap-2">

                      {/* Red — editable: cei_yellow_min */}
                      <div className={card("red")}>
                        <div className="flex items-center gap-1.5"><span className={dot("red")} /><span className={label("red")}>Red</span></div>
                        <div className={rangeText("red")}>
                          <span>&lt;&nbsp;</span>
                          <InlineInput value={thresholds.cei_yellow_min} onChange={v => set("cei_yellow_min", v)} color="red" />
                        </div>
                      </div>

                      {/* Yellow — editable: cei_green_min */}
                      <div className={card("amber")}>
                        <div className="flex items-center gap-1.5"><span className={dot("amber")} /><span className={label("amber")}>Yellow</span></div>
                        <div className={rangeText("amber")}>
                          <span>{thresholds.cei_yellow_min}&nbsp;–&nbsp;</span>
                          <InlineInput value={thresholds.cei_green_min} onChange={v => set("cei_green_min", v)} color="amber" />
                        </div>
                      </div>

                      {/* Green — derived */}
                      <div className={card("green")}>
                        <div className="flex items-center gap-1.5"><span className={dot("green")} /><span className={label("green")}>Green</span></div>
                        <p className="text-sm font-bold text-green-600">&#8805;&nbsp;{thresholds.cei_green_min}</p>
                      </div>
                    </div>
                  </div>

                  {/* Staffing */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-xs font-medium text-gray-500 mb-3">Staffing thresholds</p>
                    <div className="grid grid-cols-3 gap-2">

                      {/* Unhealthy — editable: staffing_risk_min */}
                      <div className={card("red")}>
                        <div className="flex items-center gap-1.5"><span className={dot("red")} /><span className="text-xs font-bold text-red-700 leading-tight">Unhealthy</span></div>
                        <div className={rangeText("red")}>
                          <span>0%&nbsp;–&nbsp;</span>
                          <InlineInput value={thresholds.staffing_risk_min} onChange={v => set("staffing_risk_min", v)} color="red" />
                          <span>%</span>
                        </div>
                      </div>

                      {/* Risk — editable: staffing_healthy_min */}
                      <div className={card("amber")}>
                        <div className="flex items-center gap-1.5"><span className={dot("amber")} /><span className={label("amber")}>Risk</span></div>
                        <div className={rangeText("amber")}>
                          <span>{thresholds.staffing_risk_min}%&nbsp;–&nbsp;</span>
                          <InlineInput value={thresholds.staffing_healthy_min} onChange={v => set("staffing_healthy_min", v)} color="amber" />
                          <span>%</span>
                        </div>
                      </div>

                      {/* Healthy — derived */}
                      <div className={card("green")}>
                        <div className="flex items-center gap-1.5"><span className={dot("green")} /><span className={label("green")}>Healthy</span></div>
                        <p className="text-sm font-bold text-green-600">&gt;&nbsp;{thresholds.staffing_healthy_min}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Frequency */}
          {step === 4 && (
            <div>
              <p className="text-sm text-gray-600 mb-1">How frequently do you want to generate reports? *</p>
              <p className="text-xs text-gray-400 mb-4">Your selection determines the default reporting period suggested on the Upload Files screen.</p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "monthly",   label: "MONTHLY",   desc: "Best for fast-moving programs with frequent milestones and funding reviews.", detail: "12 reports/year · Jan, Feb, Mar..." },
                  { value: "quarterly", label: "QUARTERLY", desc: "Standard cadence for EAC program reviews, aligned to fiscal quarter boundaries.", detail: "4 reports/year · Q1, Q2, Q3, Q4" },
                ].map(opt => (
                  <div key={opt.value} onClick={() => setFrequency(opt.value as "monthly" | "quarterly")}
                    className={`border-2 rounded-xl p-5 cursor-pointer transition-all
                      ${frequency === opt.value ? "border-blue-600 bg-blue-50" : "border-gray-200 hover:border-gray-300"}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-8 h-8 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${frequency === opt.value ? "border-blue-600" : "border-gray-300"}`}>
                        {frequency === opt.value && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                      </div>
                    </div>
                    <p className={`text-xs font-bold tracking-wide mb-1 ${frequency === opt.value ? "text-blue-700" : "text-gray-500"}`}>{opt.label}</p>
                    <p className="text-xs text-gray-600 mb-2 leading-relaxed">{opt.desc}</p>
                    <p className="text-xs text-gray-400">{opt.detail}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                The Upload Files screen will default to: "{frequency === "monthly" ? "May 2026" : "Q2 2026"}" — you can override this per report.
              </div>
            </div>
          )}

          {/* Step 5 — Confirm */}
          {step === 5 && (
            <div>
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-5">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-green-800">Ready to create</p>
                  <p className="text-xs text-green-600">Review your settings below. You can edit these at any time from Project Settings.</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden text-sm">
                {[
                  { label: "Project name",      value: details.name || "—" },
                  { label: "Files uploaded",     value: `${files.length} file${files.length !== 1 ? "s" : ""}` },
                  { label: "Variance threshold", value: `$${Number(thresholds.cv_threshold).toLocaleString()} or ${thresholds.variance_pct}%` },
                  { label: "FTE definition",     value: `${thresholds.fte_hours_per_week} hrs/week · ${thresholds.fte_hours_per_month} hrs/month` },
                  { label: "Report frequency",   value: frequency === "monthly" ? "Monthly (12x/year)" : "Quarterly (4x/year)" },
                ].map((row, i, arr) => (
                  <div key={i} className={`flex justify-between px-5 py-3 ${i < arr.length - 1 ? "border-b border-gray-100" : ""}`}>
                    <span className="text-gray-500">{row.label}</span>
                    <span className="font-medium text-gray-900">{row.value}</span>
                  </div>
                ))}
                <div className="px-5 py-3 border-t border-gray-100">
                  <div className="flex justify-between items-start">
                    <span className="text-gray-500">CPI / SPI</span>
                    <div className="text-xs text-right space-y-0.5 text-gray-700">
                      <div className="flex items-center gap-1 justify-end">
                        <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />Blue &gt;{thresholds.cpi_green_max}%
                        <span className="text-gray-300 mx-1">·</span>
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />Green {thresholds.cpi_green_min}%–{thresholds.cpi_green_max}%
                      </div>
                      <div className="flex items-center gap-1 justify-end">
                        <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />Yellow {thresholds.cpi_yellow_min}%–{thresholds.cpi_green_min}%
                        <span className="text-gray-300 mx-1">·</span>
                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />Red &lt;{thresholds.cpi_yellow_min}%
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-gray-100">
                  <div className="flex justify-between items-start">
                    <span className="text-gray-500">CEI / Staffing</span>
                    <div className="text-xs text-right space-y-0.5 text-gray-700">
                      <div>CEI: Red &lt;{thresholds.cei_yellow_min} · Yellow {thresholds.cei_yellow_min}–{thresholds.cei_green_min} · Green &#8805;{thresholds.cei_green_min}</div>
                      <div>Staffing: 0–{thresholds.staffing_risk_min}% · {thresholds.staffing_risk_min}–{thresholds.staffing_healthy_min}% · &gt;{thresholds.staffing_healthy_min}%</div>
                    </div>
                  </div>
                </div>
              </div>
              {error && <p className="text-red-500 text-xs mt-3">{error}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 pb-6 flex justify-between">
          <button onClick={() => step === 1 ? navigate("/select-project") : setStep(s => s - 1)}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < 5
            ? <button onClick={() => setStep(s => s + 1)} disabled={!canContinue()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                Continue →
              </button>
            : <button onClick={handleCreate} disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? "Creating..." : "Create Project →"}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
